import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  CalendarMonth as CalendarIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  LocalShipping as TruckIcon,
  Search as SearchIcon,
  SpaceDashboard as CapacityIcon,
  ViewList as ListIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  canMoveTruckToRegion,
  FboTruckCalendar,
  FboTruckDetailsDialog,
  FboTruckList,
  formatFboDate,
  getTruckDate,
  getTruckId,
  getTruckPallets,
  isTruckUnderloaded,
  normalizeFboTruckPlan,
} from '../../components/analytics/fbo';
import FormDialog from '../../components/ui/FormDialog';
import PageHeader from '../../components/ui/PageHeader';
import StatCard from '../../components/ui/StatCard';
import axios from '../../utils/axios';
import { notify } from '../../utils/notify';
import { useIsMobile } from '../../utils/responsive';

const STATUS_LABELS = {
  draft: 'Черновик',
  active: 'Активный',
  archived: 'Архив',
};

const STATUS_COLORS = {
  draft: 'default',
  active: 'success',
  archived: 'secondary',
};

const EMPTY_TRUCK = { okrug: '', ship_date: '', capacity_pallets: 16 };
const number = (value) => Number(value) || 0;
const fmt = (value, digits = 0) => new Intl.NumberFormat('ru-RU', {
  maximumFractionDigits: digits,
}).format(number(value));

const errorMessage = (error, fallback) => (
  error?.response?.data?.message
  || error?.response?.data?.error
  || error?.message
  || fallback
);

function ConfirmDialog({ open, title, message, confirmText, loading, onClose, onConfirm }) {
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Отмена</Button>
        <Button color="error" variant="contained" onClick={onConfirm} disabled={loading}>
          {loading ? 'Удаление…' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PlanList({
  plans,
  selectedId,
  loading,
  error,
  search,
  status,
  onSearch,
  onStatus,
  onSelect,
  onRetry,
}) {
  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 2,
        height: '100%',
        border: `1px solid ${theme.palette.outlineVariant || theme.palette.divider}`,
      })}
    >
      <Stack spacing={2}>
        <Typography variant="titleMedium" fontWeight={700}>Сохранённые планы</Typography>
        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          placeholder="Поиск по названию"
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Статус</InputLabel>
          <Select value={status} label="Статус" onChange={(event) => onStatus(event.target.value)}>
            <MenuItem value="all">Все статусы</MenuItem>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {loading && [...Array(4)].map((_, index) => (
          <Skeleton key={index} variant="rounded" height={82} />
        ))}
        {!loading && error && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={onRetry}>Повторить</Button>}>
            {error}
          </Alert>
        )}
        {!loading && !error && !plans.length && (
          <Alert severity="info">
            {search || status !== 'all'
              ? 'По заданным условиям планы не найдены.'
              : 'Сохранённых планов пока нет. Создайте план на странице расчёта.'}
          </Alert>
        )}
        {!loading && !error && plans.map((plan) => {
          const selected = String(plan.id) === String(selectedId);
          return (
            <Paper
              key={plan.id}
              component="button"
              type="button"
              elevation={0}
              onClick={() => onSelect(plan.id)}
              sx={(theme) => ({
                width: '100%',
                p: 1.5,
                textAlign: 'left',
                cursor: 'pointer',
                borderRadius: 2,
                border: `1px solid ${selected ? theme.palette.primary.main : (theme.palette.outlineVariant || theme.palette.divider)}`,
                bgcolor: selected ? alpha(theme.palette.primary.main, 0.1) : 'background.paper',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, selected ? 0.14 : 0.05) },
              })}
            >
              <Stack spacing={0.75}>
                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                    {plan.name || 'Без названия'}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={STATUS_COLORS[plan.status] || 'default'}
                    label={STATUS_LABELS[plan.status] || plan.status}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {fmt(plan.trucks_count)} фур · {fmt(plan.used_pallets, 1)} из {fmt(plan.capacity_pallets, 1)} палет
                </Typography>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Paper>
  );
}

export default function FboSupplyPlans() {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [savedPlans, setSavedPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [selectedTruckId, setSelectedTruckId] = useState(null);
  const [warehouseRegions, setWarehouseRegions] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [listError, setListError] = useState('');
  const [planError, setPlanError] = useState('');
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState({ name: '', status: 'draft' });
  const [createOpen, setCreateOpen] = useState(false);
  const [truckDraft, setTruckDraft] = useState(EMPTY_TRUCK);
  const [truckDraftError, setTruckDraftError] = useState('');
  const [truckDraftTouched, setTruckDraftTouched] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [truckView, setTruckView] = useState('calendar');

  const refreshPlans = useCallback(async () => {
    setLoadingList(true);
    setListError('');
    try {
      const response = await axios.get('/analytics/supply-plan/saved-plans');
      setSavedPlans(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setListError(errorMessage(error, 'Не удалось загрузить сохранённые планы'));
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadPlan = useCallback(async (id, keepTruckId = null) => {
    if (!id) return;
    setLoadingPlan(true);
    setPlanError('');
    try {
      const response = await axios.get(`/analytics/supply-plan/saved-plans/${id}`);
      setActivePlan(response.data);
      setSelectedTruckId(keepTruckId);
      setSearchParams({ id: String(id) });
    } catch (error) {
      setPlanError(errorMessage(error, 'Не удалось загрузить план'));
    } finally {
      setLoadingPlan(false);
    }
  }, [setSearchParams]);

  const applyPlan = useCallback((plan, keepTruckId = null) => {
    setActivePlan(plan);
    setSelectedTruckId(keepTruckId);
  }, []);

  useEffect(() => {
    refreshPlans();
    axios.get('/analytics/supply-plan/warehouse-regions')
      .then(({ data }) => {
        const districts = Array.isArray(data?.federal_districts) ? data.federal_districts : [];
        const short = data?.district_short || {};
        setWarehouseRegions(districts.map((okrug) => ({ okrug, label: short[okrug] || okrug })));
      })
      .catch(() => setWarehouseRegions([]));
  }, [refreshPlans]);

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) loadPlan(id);
    // URL читается один раз при открытии страницы.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredPlans = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('ru');
    return savedPlans.filter((plan) => (
      (!query || String(plan.name || '').toLocaleLowerCase('ru').includes(query))
      && (status === 'all' || plan.status === status)
    ));
  }, [savedPlans, search, status]);

  const truckPlan = useMemo(() => normalizeFboTruckPlan(
    activePlan?.truck_plan || { trucks: activePlan?.trucks || [] },
    { savedPlan: activePlan, expandCalendar: true },
  ), [activePlan]);

  const selectedTruck = useMemo(() => truckPlan.trucks.find(
    (truck) => String(getTruckId(truck)) === String(selectedTruckId),
  ) || null, [selectedTruckId, truckPlan.trucks]);

  const totals = activePlan?.totals || {};
  const trucksCount = number(totals.trucks) || truckPlan.trucks.length;
  const usedPallets = number(totals.pallets)
    || truckPlan.trucks.reduce((sum, truck) => sum + number(getTruckPallets(truck)), 0);
  const capacityPallets = number(totals.capacity_pallets)
    || truckPlan.trucks.reduce((sum, truck) => sum + number(truck.capacity_pallets), 0);
  const underloadedCount = truckPlan.trucks.filter(isTruckUnderloaded).length;
  const datesCount = new Set(truckPlan.trucks.map(getTruckDate).filter(Boolean)).size;

  const mutate = async (request, successMessage, keepTruckId = null) => {
    setBusy(true);
    setPlanError('');
    try {
      const response = await request();
      applyPlan(response.data, keepTruckId);
      await refreshPlans();
      if (successMessage) notify(successMessage, { variant: 'success' });
      return true;
    } catch (error) {
      const message = errorMessage(error, 'Не удалось сохранить изменения');
      setPlanError(message);
      notify(message, { variant: 'error' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const savePlanHeader = async () => {
    const name = editDraft.name.trim();
    if (!name) return;
    const ok = await mutate(
      () => axios.put(`/analytics/supply-plan/saved-plans/${activePlan.id}`, {
        name,
        status: editDraft.status,
      }),
      'План обновлён',
    );
    if (ok) setEditOpen(false);
  };

  const createTruck = async () => {
    setTruckDraftTouched({ okrug: true, ship_date: true, capacity_pallets: true });
    if (!truckDraft.okrug || !truckDraft.ship_date) {
      setTruckDraftError('');
      return;
    }
    if (![16, 32].includes(Number(truckDraft.capacity_pallets))) {
      setTruckDraftError('Вместимость должна быть 16 или 32 палеты');
      return;
    }
    const ok = await mutate(
      () => axios.post(`/analytics/supply-plan/saved-plans/${activePlan.id}/trucks`, truckDraft),
      'Фура добавлена',
    );
    if (ok) {
      setCreateOpen(false);
      setTruckDraft(EMPTY_TRUCK);
      setTruckDraftError('');
      setTruckDraftTouched({});
    }
  };

  const updateTruck = (truck, draft) => mutate(
    () => axios.patch(`/analytics/supply-plan/saved-plans/${activePlan.id}/trucks/${getTruckId(truck)}`, draft),
    'Фура обновлена',
    getTruckId(truck),
  );

  const moveTruck = ({ truckId, shipDate, region }) => {
    const truck = truckPlan.trucks.find((item) => String(getTruckId(item)) === String(truckId));
    if (!canMoveTruckToRegion(truck, region) || getTruckDate(truck) === shipDate) return;
    mutate(
      () => axios.patch(`/analytics/supply-plan/saved-plans/${activePlan.id}/trucks/${truckId}`, { ship_date: shipDate }),
      `Фура перенесена на ${formatFboDate(shipDate)}`,
    );
  };

  const updateItem = (item, draft) => mutate(
    () => axios.patch(`/analytics/supply-plan/saved-plans/${activePlan.id}/items/${item.id}`, {
      qty: draft.qty,
      truck_id: draft.truck_id,
    }),
    'Товар обновлён',
    draft.truck_id || item.truck_id,
  );

  const addItem = (truck, draft) => mutate(
    () => axios.post(`/analytics/supply-plan/saved-plans/${activePlan.id}/trucks/${getTruckId(truck)}/items`, draft),
    'Товар добавлен',
    getTruckId(truck),
  );

  const searchProducts = useCallback(async (query) => {
    if (!activePlan?.id) return [];
    const response = await axios.get(
      `/analytics/supply-plan/saved-plans/${activePlan.id}/products/search`,
      { params: { q: query, limit: 25 }, silent: true },
    );
    return Array.isArray(response.data?.items) ? response.data.items : [];
  }, [activePlan?.id]);

  const executeConfirm = async () => {
    if (!confirm) return;
    let request;
    let keepTruckId = null;
    if (confirm.type === 'truck') {
      request = () => axios.delete(`/analytics/supply-plan/saved-plans/${activePlan.id}/trucks/${getTruckId(confirm.value)}`);
    } else if (confirm.type === 'item') {
      request = () => axios.delete(`/analytics/supply-plan/saved-plans/${activePlan.id}/items/${confirm.value.id}`);
      keepTruckId = confirm.value.truck_id;
    } else {
      request = () => axios.delete(
        `/analytics/supply-plan/saved-plans/${activePlan.id}/regions/${encodeURIComponent(confirm.value.value)}`,
      );
    }
    const ok = await mutate(request, 'Изменения сохранены', keepTruckId);
    if (ok) {
      if (confirm.type === 'truck') setSelectedTruckId(null);
      setConfirm(null);
    }
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const base = axios.defaults.baseURL || '/api';
      const response = await fetch(
        `${base}/analytics/supply-plan/saved-plans/${activePlan.id}/export`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) throw new Error('Ошибка выгрузки');
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = `План_FBO_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      notify(errorMessage(error, 'Не удалось скачать план'), { variant: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const showList = !isMobile || !activePlan;
  const showDetail = !isMobile || Boolean(activePlan);

  return (
    <Box>
      <PageHeader
        title="Планы поставок FBO"
        subtitle="Управляйте фурами, датами отправки и составом сохранённых планов"
        actions={(
          <Button component={RouterLink} to="/analytics/supply-plan" variant="outlined">
            К расчёту
          </Button>
        )}
      />

      <Grid container spacing={2}>
        {showList && (
          <Grid item xs={12} lg={3}>
            <PlanList
              plans={filteredPlans}
              selectedId={activePlan?.id}
              loading={loadingList}
              error={listError}
              search={search}
              status={status}
              onSearch={setSearch}
              onStatus={setStatus}
              onSelect={loadPlan}
              onRetry={refreshPlans}
            />
          </Grid>
        )}

        {showDetail && (
          <Grid item xs={12} lg={9}>
            {loadingPlan && (
              <Stack spacing={2}>
                <Skeleton variant="rounded" height={130} />
                <Grid container spacing={2}>
                  {[...Array(4)].map((_, index) => (
                    <Grid item xs={6} md={3} key={index}><Skeleton variant="rounded" height={150} /></Grid>
                  ))}
                </Grid>
                <Skeleton variant="rounded" height={320} />
              </Stack>
            )}

            {!loadingPlan && !activePlan && (
              <Paper sx={{ p: 5, textAlign: 'center' }}>
                <TruckIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="titleLarge" sx={{ mb: 1 }}>Выберите план</Typography>
                <Typography color="text.secondary">Детали и календарь фур появятся здесь.</Typography>
              </Paper>
            )}

            {!loadingPlan && activePlan && (
              <Stack spacing={2}>
                {isMobile && (
                  <Button
                    startIcon={<BackIcon />}
                    onClick={() => {
                      setActivePlan(null);
                      setSearchParams({});
                    }}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Все планы
                  </Button>
                )}

                <Paper
                  elevation={0}
                  sx={(theme) => ({
                    p: { xs: 2, md: 3 },
                    border: `1px solid ${theme.palette.outlineVariant || theme.palette.divider}`,
                  })}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="headlineSmall" fontWeight={700}>{activePlan.name || 'Без названия'}</Typography>
                        <Chip
                          size="small"
                          color={STATUS_COLORS[activePlan.status] || 'default'}
                          label={STATUS_LABELS[activePlan.status] || activePlan.status}
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        Откройте фуру, чтобы изменить дату, вместимость или состав.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => {
                          setEditDraft({ name: activePlan.name || '', status: activePlan.status || 'draft' });
                          setEditOpen(true);
                        }}
                        disabled={busy}
                      >
                        Название и статус
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={downloadExcel}
                        disabled={downloading || busy}
                      >
                        {downloading ? 'Выгрузка…' : 'Excel'}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>

                {planError && (
                  <Alert severity="error" onClose={() => setPlanError('')}>{planError}</Alert>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <StatCard title="Фуры" value={fmt(trucksCount)} icon={<TruckIcon />} color="primary" />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StatCard
                      title="Палеты"
                      value={fmt(usedPallets, 1)}
                      subtitle={`из ${fmt(capacityPallets, 1)} мест`}
                      icon={<CapacityIcon />}
                      color="secondary"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StatCard title="Недогружено" value={fmt(underloadedCount)} icon={<WarningIcon />} color="warning" />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StatCard title="Даты отправки" value={fmt(datesCount)} icon={<CalendarIcon />} color="info" />
                  </Grid>
                </Grid>

                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="titleSmall" fontWeight={700}>
                        {truckView === 'calendar' ? 'Календарь фур' : 'Список фур'}
                      </Typography>
                      <Tooltip title="Зелёный — заполнено, жёлтый — есть недогруз">
                        <Chip size="small" variant="outlined" label="Загрузка" />
                      </Tooltip>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={truckView}
                        onChange={(_, value) => { if (value) setTruckView(value); }}
                        aria-label="Вид фур"
                      >
                        <ToggleButton value="calendar" aria-label="Календарь фур">
                          <CalendarIcon fontSize="small" sx={{ mr: 0.75 }} />
                          Календарь
                        </ToggleButton>
                        <ToggleButton value="list" aria-label="Список фур">
                          <ListIcon fontSize="small" sx={{ mr: 0.75 }} />
                          Список
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setTruckDraft(EMPTY_TRUCK);
                          setTruckDraftError('');
                          setTruckDraftTouched({});
                          setCreateOpen(true);
                        }}
                        disabled={busy}
                      >
                        Добавить фуру
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>

                {truckView === 'calendar' ? (
                  <FboTruckCalendar
                    truckPlan={truckPlan}
                    readOnly={false}
                    editable
                    dndEnabled={!isMobile}
                    showDndHint={!isMobile}
                    busy={busy}
                    emptyMessage="В плане пока нет фур. Добавьте первую фуру."
                    onTruckOpen={setSelectedTruckId}
                    onRegionRemove={(region) => setConfirm({ type: 'region', value: region })}
                    onTruckDrop={moveTruck}
                  />
                ) : (
                  <FboTruckList
                    truckPlan={truckPlan}
                    planKey={activePlan.id}
                    readOnly={false}
                    editable
                    busy={busy}
                    emptyMessage="В плане пока нет фур. Добавьте первую фуру."
                    onTruckOpen={setSelectedTruckId}
                  />
                )}
              </Stack>
            )}
          </Grid>
        )}
      </Grid>

      <FboTruckDetailsDialog
        open={Boolean(selectedTruck)}
        truck={selectedTruck}
        onClose={() => setSelectedTruckId(null)}
        fullScreen={isMobile}
        readOnly={false}
        editable
        dndEnabled={!isMobile}
        busy={busy}
        trucks={truckPlan.trucks}
        onTruckUpdate={updateTruck}
        onTruckDelete={(truck) => setConfirm({ type: 'truck', value: truck })}
        onItemUpdate={updateItem}
        onItemDelete={(item) => setConfirm({ type: 'item', value: item })}
        onItemAdd={addItem}
        onProductSearch={searchProducts}
      />

      <FormDialog
        open={editOpen}
        fullScreen={isMobile}
        onClose={() => setEditOpen(false)}
        title="Название и статус"
        subtitle="Изменения сохранятся только в выбранном плане"
        submitText="Сохранить"
        loading={busy}
        disableSubmit={!editDraft.name.trim()}
        onSubmit={savePlanHeader}
      >
        <TextField
          autoFocus
          fullWidth
          label="Название плана"
          value={editDraft.name}
          onChange={(event) => setEditDraft((current) => ({ ...current, name: event.target.value }))}
          error={!editDraft.name.trim()}
          helperText={!editDraft.name.trim() ? 'Введите название' : ''}
        />
        <FormControl fullWidth>
          <InputLabel>Статус</InputLabel>
          <Select
            label="Статус"
            value={editDraft.status}
            onChange={(event) => setEditDraft((current) => ({ ...current, status: event.target.value }))}
          >
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </FormDialog>

      <FormDialog
        open={createOpen}
        fullScreen={isMobile}
        maxWidth="sm"
        onClose={() => {
          setCreateOpen(false);
          setTruckDraftError('');
          setTruckDraftTouched({});
        }}
        title="Добавить фуру"
        subtitle="Выберите округ, дату отправки и вместимость"
        submitText="Добавить фуру"
        loadingText="Добавление…"
        loading={busy}
        disableSubmit={!warehouseRegions.length || !truckDraft.okrug || !truckDraft.ship_date}
        onSubmit={createTruck}
      >
        {truckDraftError && <Alert severity="error">{truckDraftError}</Alert>}
        <TextField
          select
          fullWidth
          autoFocus
          label="Федеральный округ"
          value={truckDraft.okrug}
          error={!warehouseRegions.length || (truckDraftTouched.okrug && !truckDraft.okrug)}
          helperText={
            !warehouseRegions.length
              ? 'Справочник округов недоступен. Закройте окно и попробуйте снова.'
              : truckDraftTouched.okrug && !truckDraft.okrug
                ? 'Выберите федеральный округ'
                : ' '
          }
          FormHelperTextProps={{ sx: { minHeight: 20, m: 0, mt: 0.5 } }}
          onBlur={() => setTruckDraftTouched((current) => ({ ...current, okrug: true }))}
          onChange={(event) => {
            setTruckDraft((current) => ({ ...current, okrug: event.target.value }));
            setTruckDraftError('');
          }}
        >
          {warehouseRegions.map((region) => (
            <MenuItem key={region.okrug} value={region.okrug}>
              {region.label} — {region.okrug}
            </MenuItem>
          ))}
        </TextField>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? '1fr'
              : 'minmax(0, 1fr) minmax(0, 1fr)',
            columnGap: 2,
            rowGap: 2,
          }}
        >
          <TextField
            fullWidth
            type="date"
            label="Дата отправки"
            InputLabelProps={{ shrink: true }}
            value={truckDraft.ship_date}
            error={truckDraftTouched.ship_date && !truckDraft.ship_date}
            helperText={truckDraftTouched.ship_date && !truckDraft.ship_date ? 'Выберите дату отправки' : ' '}
            FormHelperTextProps={{ sx: { minHeight: 20, m: 0, mt: 0.5 } }}
            onBlur={() => setTruckDraftTouched((current) => ({ ...current, ship_date: true }))}
            onChange={(event) => {
              setTruckDraft((current) => ({ ...current, ship_date: event.target.value }));
              setTruckDraftError('');
            }}
          />
          <TextField
            select
            fullWidth
            label="Вместимость"
            value={truckDraft.capacity_pallets}
            onChange={(event) => setTruckDraft((current) => ({ ...current, capacity_pallets: event.target.value }))}
            helperText="Количество палет в фуре"
            FormHelperTextProps={{ sx: { minHeight: 20, m: 0, mt: 0.5 } }}
          >
            <MenuItem value={16}>16 палет</MenuItem>
            <MenuItem value={32}>32 палеты</MenuItem>
          </TextField>
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={Boolean(confirm)}
        loading={busy}
        title={confirm?.type === 'region' ? 'Удалить регион из плана?' : confirm?.type === 'truck' ? 'Удалить фуру?' : 'Удалить товар?'}
        message={
          confirm?.type === 'region'
            ? `Все фуры и товары региона «${confirm.value.label || confirm.value.value}» будут удалены из этого плана.`
            : confirm?.type === 'truck'
              ? 'Фура и весь её состав будут удалены. Это действие нельзя отменить.'
              : 'Товар будет удалён из состава фуры.'
        }
        confirmText={confirm?.type === 'region' ? 'Удалить регион' : 'Удалить'}
        onClose={() => setConfirm(null)}
        onConfirm={executeConfirm}
      />
    </Box>
  );
}
